import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Megaphone,
  TrendingUp,
  Eye,
  MousePointer,
  DollarSign,
  Plus,
  Pause,
  Play,
  Trash2,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Store,
  FileImage,
  Upload,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adsAPI, productsAPI } from '../services/api';
import { formatPrice, getImageUrl } from '../utils/helpers';
import { toast } from '../hooks/use-toast';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const statusConfig = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  active: { label: 'Activo', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  paused: { label: 'Pausado', color: 'bg-gray-100 text-gray-800', icon: Pause },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-800', icon: XCircle },
  completed: { label: 'Completado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
};

const AdvertiserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [chartCampaignId, setChartCampaignId] = useState(null);
  const [chartSeries, setChartSeries] = useState([]);
  const [newCampaign, setNewCampaign] = useState({
    ad_type: 'product',
    product_id: '',
    budget: 500,
    target_radius_km: 50,
    budget_type: 'total',
    daily_budget: 100,
    end_date: '',
    target_url: '',
  });
  const [bannerFile, setBannerFile] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [campaignsData, statsData, productsData] = await Promise.all([
        adsAPI.getMyCampaigns(),
        adsAPI.getMyStats(),
        productsAPI.getAll({ seller_id: user?._id })
      ]);
      setCampaigns(campaignsData);
      setStats(statsData);
      
      // Filter products that don't have active campaigns
      const activeCampaignProductIds = campaignsData
        .filter(c => ['pending', 'active'].includes(c.status))
        .map(c => c.product_id);
      const availableProducts = productsData.filter(p => 
        p.seller_id === user?._id && !activeCampaignProductIds.includes(p._id)
      );
      setMyProducts(availableProducts);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    const { ad_type } = newCampaign;
    if (ad_type === 'product' && !newCampaign.product_id) {
      toast({ title: 'Error', description: 'Selecciona un producto', variant: 'destructive' });
      return;
    }
    
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('ad_type', ad_type);
      formData.append('budget', newCampaign.budget);
      formData.append('budget_type', newCampaign.budget_type);
      formData.append('daily_budget', newCampaign.daily_budget);
      formData.append('target_radius_km', newCampaign.target_radius_km);
      if (newCampaign.end_date) formData.append('end_date', newCampaign.end_date);
      if (ad_type === 'product') formData.append('product_id', newCampaign.product_id);
      if (ad_type === 'store') formData.append('target_url', `/store/${user?._id}`);
      if (ad_type === 'banner') {
        if (newCampaign.target_url) formData.append('target_url', newCampaign.target_url);
        if (bannerFile) formData.append('banner_file', bannerFile);
      }

      await adsAPI.create(formData);
      toast({ title: 'Campana creada', description: 'Se descontó el saldo de tu billetera. Pendiente de aprobación.' });
      setShowCreateForm(false);
      setNewCampaign({ ad_type: 'product', product_id: '', budget: 500, target_radius_km: 50, budget_type: 'total', daily_budget: 100, end_date: '', target_url: '' });
      setBannerFile(null);
      loadData();
    } catch (err) {
      toast({ 
        title: 'Error', 
        description: err.response?.data?.detail || 'No se pudo crear la campana', 
        variant: 'destructive' 
      });
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (campaignId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await adsAPI.update(campaignId, { status: newStatus });
      toast({ title: 'Estado actualizado' });
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo actualizar', variant: 'destructive' });
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('¿Eliminar esta campaña?')) return;
    try {
      await adsAPI.delete(campaignId);
      toast({ title: 'Campaña eliminada' });
      loadData();
    } catch (err) {
      toast({ 
        title: 'Error', 
        description: err.response?.data?.detail || 'No se pudo eliminar', 
        variant: 'destructive' 
      });
    }
  };

  const toggleChart = async (campaignId) => {
    if (chartCampaignId === campaignId) {
      setChartCampaignId(null);
      setChartSeries([]);
      return;
    }
    try {
      const data = await adsAPI.getImpressionsSeries(campaignId);
      setChartSeries(data.series || []);
      setChartCampaignId(campaignId);
    } catch {
      toast({ title: 'Error', description: 'No se pudo cargar el gráfico', variant: 'destructive' });
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-zl-brown">Mis Campanas</h1>
              <p className="text-gray-600">Gestiona tus anuncios patrocinados</p>
            </div>
            <Button 
              onClick={() => setShowCreateForm(true)}
              className="bg-zl-orange hover:bg-zl-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Campaña
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Eye className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_impressions.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Impresiones</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MousePointer className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_clicks.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Clics</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.ctr}%</p>
                    <p className="text-sm text-gray-500">CTR</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatPrice(stats.total_spent)}</p>
                    <p className="text-sm text-gray-500">Gastado</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Create Campaign Modal */}
          {showCreateForm && (
            <Card className="p-6 border-2 border-zl-orange">
              <h3 className="text-lg font-semibold mb-4">Nueva Campana Publicitaria</h3>
              <form onSubmit={handleCreateCampaign} className="space-y-4">
                {/* Ad Type Selector */}
                <div>
                  <Label className="mb-2 block">Que deseas promocionar?</Label>
                  <div className="grid grid-cols-3 gap-3" data-testid="ad-type-selector">
                    {[
                      { key: 'product', icon: Package, label: 'Producto', desc: 'Un producto tuyo' },
                      { key: 'store', icon: Store, label: 'Mi Tienda', desc: 'Tu perfil de vendedor' },
                      { key: 'banner', icon: FileImage, label: 'Banner', desc: 'Imagen personalizada' },
                    ].map(t => {
                      const Icon = t.icon;
                      return (
                        <button key={t.key} type="button"
                          onClick={() => setNewCampaign(c => ({ ...c, ad_type: t.key }))}
                          className={`p-3 rounded-xl border-2 text-center transition-all ${
                            newCampaign.ad_type === t.key ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                          }`} data-testid={`ad-type-${t.key}`}>
                          <Icon className={`w-6 h-6 mx-auto mb-1 ${newCampaign.ad_type === t.key ? 'text-orange-500' : 'text-gray-400'}`} />
                          <p className="font-semibold text-sm">{t.label}</p>
                          <p className="text-[10px] text-gray-400">{t.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dynamic Fields by ad_type */}
                {newCampaign.ad_type === 'product' && (
                  <div>
                    <Label>Producto a promocionar</Label>
                    {myProducts.length === 0 ? (
                      <p className="text-sm text-gray-500 mt-2">
                        No tienes productos disponibles. 
                        <a href="/seller/products" className="text-zl-orange ml-1">Crea uno aqui</a>
                      </p>
                    ) : (
                      <Select 
                        value={newCampaign.product_id}
                        onValueChange={(v) => setNewCampaign({ ...newCampaign, product_id: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecciona un producto" />
                        </SelectTrigger>
                        <SelectContent>
                          {myProducts.map(product => (
                            <SelectItem key={product._id} value={product._id}>
                              {product.title} - {formatPrice(product.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {newCampaign.ad_type === 'store' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Store className="w-5 h-5" />
                      <p className="text-sm font-medium">Se promocionara tu perfil de tienda</p>
                    </div>
                    <p className="text-xs text-blue-500 mt-1">Los usuarios veran tu tienda destacada en el feed</p>
                  </div>
                )}

                {newCampaign.ad_type === 'banner' && (
                  <div className="space-y-3">
                    <div>
                      <Label>Imagen del Banner</Label>
                      <label className="mt-1 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-orange-400 transition-colors" data-testid="banner-upload">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => setBannerFile(e.target.files?.[0] || null)} />
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-500">{bannerFile ? bannerFile.name : 'Seleccionar imagen'}</span>
                      </label>
                    </div>
                    <div>
                      <Label>URL de destino</Label>
                      <Input placeholder="https://tu-sitio.com/promo" value={newCampaign.target_url}
                        onChange={(e) => setNewCampaign(c => ({ ...c, target_url: e.target.value }))}
                        className="mt-1" data-testid="target-url-input" />
                    </div>
                  </div>
                )}

                {/* Budget Type Selector */}
                <div>
                  <Label className="mb-2 block">Tipo de presupuesto</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button"
                      onClick={() => setNewCampaign(c => ({...c, budget_type: 'daily'}))}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${newCampaign.budget_type === 'daily' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid="budget-type-daily"
                    >
                      <p className="font-semibold text-sm">Presupuesto Diario</p>
                      <p className="text-[11px] text-gray-400">Se cobra 1 dia por adelantado</p>
                    </button>
                    <button type="button"
                      onClick={() => setNewCampaign(c => ({...c, budget_type: 'total'}))}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${newCampaign.budget_type === 'total' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid="budget-type-total"
                    >
                      <p className="font-semibold text-sm">Presupuesto Total</p>
                      <p className="text-[11px] text-gray-400">Se cobra todo al crear</p>
                    </button>
                  </div>
                </div>

                {/* Dynamic budget inputs */}
                {newCampaign.budget_type === 'daily' ? (
                  <div>
                    <Label>Presupuesto diario (MXN)</Label>
                    <Input type="number" min="100" value={newCampaign.daily_budget}
                      onChange={(e) => setNewCampaign(c => ({...c, daily_budget: parseFloat(e.target.value) || 0}))}
                      className="mt-1" data-testid="daily-budget-input" />
                    <p className="text-xs text-gray-500 mt-1">Minimo: $100 MXN/dia. Se cobrara 1 dia por adelantado de tu billetera.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label>Presupuesto total (MXN)</Label>
                      <Input type="number" min="100" value={newCampaign.budget}
                        onChange={(e) => setNewCampaign(c => ({...c, budget: parseFloat(e.target.value) || 0}))}
                        className="mt-1" data-testid="total-budget-input" />
                      <p className="text-xs text-gray-500 mt-1">Minimo: $100 MXN</p>
                    </div>
                    <div>
                      <Label>Fecha de fin (opcional)</Label>
                      <Input type="date" value={newCampaign.end_date}
                        onChange={(e) => setNewCampaign(c => ({...c, end_date: e.target.value}))}
                        className="mt-1" data-testid="end-date-input" />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Radio de alcance (km)</Label>
                  <Input type="number" min="5" max="500" value={newCampaign.target_radius_km}
                    onChange={(e) => setNewCampaign(c => ({...c, target_radius_km: parseFloat(e.target.value) || 50}))}
                    className="mt-1" />
                </div>

                {/* Cost summary */}
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-amber-800 mb-1">Cobro inmediato de tu Billetera:</p>
                  <p className="text-lg font-black text-amber-700">
                    {formatPrice(newCampaign.budget_type === 'daily' ? (newCampaign.daily_budget || 0) : (newCampaign.budget || 0))}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Costo por clic: $0.50 MXN | Clics estimados: ~{Math.floor((newCampaign.budget_type === 'daily' ? (newCampaign.daily_budget || 0) : (newCampaign.budget || 0)) / 0.5)}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={creating || (newCampaign.ad_type === 'product' && (!newCampaign.product_id || myProducts.length === 0))} className="bg-zl-orange hover:bg-zl-orange-600">
                    {creating ? 'Procesando...' : 'Pagar y Crear Campana'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Campaigns List */}
          <Card className="overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Mis Campañas</h3>
              <Button variant="ghost" size="sm" onClick={loadData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="p-12 text-center">
                <Megaphone className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No tienes campañas aún</p>
                <Button 
                  onClick={() => setShowCreateForm(true)} 
                  className="mt-4 bg-zl-orange hover:bg-zl-orange-600"
                >
                  Crear primera campaña
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {campaigns.map(campaign => {
                  const status = statusConfig[campaign.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  const progress = campaign.budget > 0 ? (campaign.spent / campaign.budget) * 100 : 0;
                  
                  return (
                    <div key={campaign._id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start gap-4">
                        {/* Product image */}
                        <img
                          src={getImageUrl(campaign.product?.images?.[0])}
                          alt={campaign.product?.title}
                          className="w-20 h-20 rounded-lg object-cover bg-gray-100"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/80'}
                        />
                        
                        {/* Campaign info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium">{campaign.product?.title || 'Producto'}</h4>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${status.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                {status.label}
                              </span>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-2">
                              {campaign.status === 'active' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleToggleStatus(campaign._id, campaign.status)}
                                  title="Pausar"
                                >
                                  <Pause className="w-4 h-4" />
                                </Button>
                              )}
                              {campaign.status === 'paused' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleToggleStatus(campaign._id, campaign.status)}
                                  title="Activar"
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                              )}
                              {['pending', 'paused', 'rejected'].includes(campaign.status) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(campaign._id)}
                                  className="text-red-500 hover:text-red-700"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Stats */}
                          <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Impresiones</p>
                              <p className="font-semibold">{campaign.impressions.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Clics</p>
                              <p className="font-semibold">{campaign.clicks.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">CTR</p>
                              <p className="font-semibold">
                                {campaign.impressions > 0 
                                  ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) 
                                  : 0}%
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Gastado</p>
                              <p className="font-semibold">{formatPrice(campaign.spent)}</p>
                            </div>
                          </div>
                          
                          {/* Budget progress */}
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Presupuesto</span>
                              <span>{formatPrice(campaign.spent)} / {formatPrice(campaign.budget)}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-zl-orange h-2 rounded-full transition-all"
                                style={{ width: `${Math.min(progress, 100)}%` }}
                              />
                            </div>
                          </div>

                          {/* Chart toggle */}
                          {campaign.impressions > 0 && (
                            <div className="mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleChart(campaign._id)}
                                data-testid={`chart-toggle-${campaign._id}`}
                              >
                                <TrendingUp className="w-4 h-4 mr-1" />
                                {chartCampaignId === campaign._id ? 'Ocultar grafico' : 'Ver grafico'}
                              </Button>
                              {chartCampaignId === campaign._id && chartSeries.length > 0 && (
                                <div className="mt-3 h-48 bg-gray-50 rounded-lg p-2" data-testid="advertiser-impressions-chart">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartSeries}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                      <Tooltip />
                                      <Line type="monotone" dataKey="impressions" name="Impresiones" stroke="#F97316" strokeWidth={2} dot={false} />
                                      <Line type="monotone" dataKey="clicks" name="Clics" stroke="#3B82F6" strokeWidth={2} dot={false} />
                                      <Legend />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {campaign.rejection_reason && (
                            <p className="mt-2 text-sm text-red-600">
                              Rechazado: {campaign.rejection_reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
    </div>
  );
};

export default AdvertiserDashboard;
